/* eslint-disable class-methods-use-this */
import type Vue from 'vue';
import { env } from './env';

enum Runtime {
  Prod,
  Staging,
  Test,
  Dev,
  Local,
}

interface ICATErrorModel {
  level?: 'error' | 'info' | 'warn' | 'debug';
  category?: string;
  tags?: any;
}

class Monitor {
  private static readonly TTI_STORAGE_KEY: string = 'tti_report_flag';

  config: object;

  constructor(isDebug: boolean) {
    this.config = {
      project: 'com.sankuai.fspfepayresource.paydefer',
      pageUrl: `${window.location.protocol}//${window.location.host}${window.location.pathname}`,
      devMode: isDebug,
      resource: {
        sampleApi: 1,
        delay: 0,
      },
      page: {
        auto: true,
        sample: 1,
        delay: 0,
        sensoryIndex: false,
      },
      error: {
        sample: 1,
        combo: false,
        formatUnhandledRejection: true,
      },
      ajax: {
        flag: true,
        duration: 8000,
      },
      metric: {
        sample: 1,
        combo: true,
      },
      logan: {
        enable: false,
      },
      noScriptError: false,
      onErrorPush: (error: any) =>
        error.updateTags({
          ...this.defaultInfo,
        }),
    };

    this.start();
  }

  get defaultInfo() {
    return {
      ...env.statCommonParams,
      scorePlanId: env.coreBusinessParams.scorePlanId,
      entry: env.entry,
      openType: env.openType,
      hideHalfPage: env.hideHalfPage,
      reversionId: env.reversionId,
      payType: env.payType,
      openStatus: env.openStatus,
      creditType: env.creditType,
    };
  }

  captureMessage(error: string | SubErrorType, config?: ICATErrorModel) {
    console.error(error);
    if (Owl) {
      const baseConfig: ICATErrorModel = {
        level: 'info',
        category: 'custom_info',
      };

      Owl.addError(error, {
        ...baseConfig,
        ...config,
      });
    }
  }

  // 上报日志 info 级别
  captureInfo(message: string | SubErrorType, tags?: any) {
    this.captureMessage(message, {
      tags,
      level: 'info',
      category: 'custom_info',
    });
  }

  captureAjaxErrorMessage(name: string, msg: Record<string, any> = {}, tags: Record<string, any> = {}) {
    this.captureMessage(
      {
        name,
        msg,
      },
      {
        tags,
        level: 'error',
        category: 'ajaxError',
      }
    );
  }

  captureJSErrorMessage(name: string, msg: Record<string, any> = {}, tags: Record<string, any> = {}) {
    this.captureMessage(
      {
        name,
        msg,
      },
      {
        tags,
        level: 'error',
        category: 'jsError',
      }
    );
  }

  captureResourceInfoMessage(message: string | SubErrorType, tags?: any) {
    this.captureMessage(message, {
      tags,
      level: 'info',
      category: 'resourceError',
    });
  }

  reportMetric(key: string, tags = {}, value?: any): void {
    if (Owl) {
      const metricManager = Owl.metricManager;
      metricManager.setTags({
        ...this.defaultInfo,
        ...tags,
      });
      metricManager.setMetric(key, value || 1);
      // 强制上报所有
      metricManager.report();
    }
  }

  /**
   * 上报测速点
   * 每个测速点只上报一次
   */
  reportPoint(position: number, duration = 0): void {
    if (!window.sessionStorage || window.sessionStorage.getItem(`${Monitor.TTI_STORAGE_KEY}_${position}`) === '1') {
      return;
    }
    if (Owl) {
      const param: AddPointConfig = { position };
      if (duration) {
        param.duration = duration || 0;
      }
      Owl.addPoint(param);
      Owl.sendPoints();
      window.sessionStorage.setItem(`${Monitor.TTI_STORAGE_KEY}_${position}`, '1');
    }
  }

  vueErrorHandler(err: Error, vm: Vue, msg: string) {
    if (!err) {
      console.warn('未捕获异常');
      return;
    }
    // 为保证报错在控制台展示
    console.error(err);
    const error = {
      name: `[vueErrorHandler]${msg}${err.name}`,
      msg: { res: err.stack ?? err.message, url: err.name }, // 只上报类型，vm 里的数据由于数据量大不再上报
    };
    const opts = {
      category: 'jsError',
    };
    if (Owl) {
      Owl.addError(error, {
        ...opts,
      });
    }
  }

  private start() {
    if (Owl) {
      Owl.start(this.config);
    }
  }
}

export const monitor = new Monitor(env.runtime !== Runtime.Prod);
